import { Fragment } from 'react';
import { NavLink, Link } from 'react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  LayoutDashboard,
  MessageSquare,
  Workflow,
  Target,
  Brain,
  Settings,
  ClipboardList,
  Lightbulb,
  FolderKanban,
  BriefcaseBusiness,
  KeyRound,
  ClipboardCheck,
  LogOut,
} from 'lucide-react';
import { listWorkflowRuns, getUpdateCheck, getAuthSession, logoutAuthUser } from '@/lib/api';
import { cn } from '@/lib/utils';
import { NotificationBell } from '@/components/layout/NotificationBell';

/**
 * The operator's top-level surfaces, in reading order.
 *
 * THIS ARRAY'S SHAPE IS PINNED BY A GATE. scripts/validate-ui-api-surface-map.mjs
 * locates this array by scanning this file's source text for its opening and closing
 * delimiters, then matches each entry with a regex that requires the route field to
 * precede the label field with no closing brace between them. It asserts every route
 * here exists in the App router AND in both tables of
 * docs/catalogs/ui-api-surface-map.md. So: keep the array flat, keep that field
 * order, and run `bun run validate:ui-api-surface-map` after editing it.
 *
 * Do not quote those delimiters in prose anywhere in this file — the scanner takes
 * the FIRST textual match, so a mention in a comment above the array makes it slice
 * the comment instead and extract nothing. That happened while this note was being
 * written, and the gate reported success on zero tabs; it now fails on an empty
 * extraction instead of passing vacuously.
 *
 * THE ORDER IS ALSO A CONTRACT. e2e/navigation.spec.ts asserts that the nav exposes
 * every one of these options, in this sequence, as a VISIBLE and clickable link
 * ("top nav exposes every expected menu option in order"). An earlier version of this
 * change moved the four operator/setup surfaces behind a "More" popover to thin out
 * twelve equal-weight tabs; e2e caught it — three failures, because a popover's
 * contents do not exist in the DOM while closed and the mobile row's copies are
 * display:none at desktop width.
 *
 * `cluster` resolves that without hiding anything: it is presentation only (the gate's
 * regex ignores trailing fields) and only decides where a thin divider is drawn. The
 * reader gets grouping, the contract keeps every option one click away.
 */
const tabs = [
  { to: '/chat', end: false, icon: MessageSquare, label: 'Chat', cluster: 'work' },
  { to: '/dashboard', end: true, icon: LayoutDashboard, label: 'Dashboard', cluster: 'work' },
  { to: '/projects', end: false, icon: FolderKanban, label: 'Projects', cluster: 'operate' },
  { to: '/workbench', end: false, icon: BriefcaseBusiness, label: 'Workbench', cluster: 'operate' },
  { to: '/local-enrollment', end: true, icon: KeyRound, label: 'Enrollment', cluster: 'operate' },
  {
    to: '/operations/aaron-live-deployment',
    end: true,
    icon: ClipboardCheck,
    label: 'Live Ops',
    cluster: 'operate',
  },
  { to: '/workflows', end: false, icon: Workflow, label: 'Workflows', cluster: 'deliver' },
  { to: '/outcomes', end: false, icon: Target, label: 'Outcomes', cluster: 'deliver' },
  { to: '/delivery', end: true, icon: ClipboardList, label: 'Delivery', cluster: 'deliver' },
  { to: '/g-brain', end: true, icon: Brain, label: 'G-Brain', cluster: 'learn' },
  { to: '/intelligence', end: true, icon: Lightbulb, label: 'Intel', cluster: 'learn' },
  { to: '/settings', end: false, icon: Settings, label: 'Settings', cluster: 'admin' },
] as const;

type NavTab = (typeof tabs)[number];

/** Every tab, in the order e2e/navigation.spec.ts pins. */
export const NAV_TABS: readonly NavTab[] = tabs;

/**
 * True when a thin divider precedes this tab — i.e. it opens a new cluster.
 *
 * Derived rather than hand-listed so a reordered or reassigned tab cannot leave a
 * divider stranded mid-cluster.
 */
export function startsCluster(index: number): boolean {
  if (index === 0) return false;
  return tabs[index]?.cluster !== tabs[index - 1]?.cluster;
}

/**
 * Where to land after signing out.
 *
 * A bare '/' would break a base-path deployment (VITE_BASE_PATH), dropping the
 * operator outside the app mount entirely — the same variable App.tsx feeds to
 * BrowserRouter's basename.
 */
export function signedOutDestination(basePath: string | undefined): string {
  const trimmed = (basePath ?? '').trim();
  return trimmed === '' ? '/' : trimmed;
}

export function TopNav(): React.ReactElement {
  const { data: runningRuns } = useQuery({
    queryKey: ['workflowRuns', { status: 'running' }],
    queryFn: () => listWorkflowRuns({ status: 'running', limit: 1 }),
    refetchInterval: 10_000,
  });
  const hasRunning = (runningRuns?.length ?? 0) > 0;

  const { data: updateCheck } = useQuery({
    queryKey: ['update-check'],
    queryFn: getUpdateCheck,
    staleTime: 60 * 60 * 1000,
    refetchInterval: 60 * 60 * 1000,
    retry: false,
  });

  // Shares the ['authSession'] key AuthGate already populated, so this is a cache
  // read rather than a second request — TopNav only renders inside that gate.
  const { data: session } = useQuery({ queryKey: ['authSession'], queryFn: getAuthSession });
  const operatorLabel = session?.operator?.name ?? session?.operator?.id ?? null;

  const signOut = useMutation({
    mutationFn: logoutAuthUser,
    onSuccess: () => {
      // A full document load, not navigate() + queryClient.clear(). The cache holds
      // operator-scoped data (projects, conversations, outcomes) and clearing it
      // while still mounted inside AuthGate races the route change — the operator
      // sees LoginScreen flash on the way out. Reloading discards every trace of
      // in-memory state in one step and cannot leave a half-cleared cache behind.
      window.location.assign(
        signedOutDestination(import.meta.env.VITE_BASE_PATH as string | undefined)
      );
    },
  });

  const renderTab = ({ to, end, icon: Icon, label }: NavTab): React.ReactElement => (
    <NavLink
      key={to}
      to={to}
      end={end}
      className={({ isActive }: { isActive: boolean }): string =>
        cn(
          'flex shrink-0 items-center gap-2 border-b-2 px-3 py-3 text-sm font-medium transition-colors',
          isActive
            ? 'border-primary text-primary'
            : 'border-transparent text-text-secondary hover:text-text-primary'
        )
      }
    >
      <Icon className="h-4 w-4" />
      {label}
      {to === '/dashboard' && hasRunning && (
        <span className="flex h-2 w-2 rounded-full bg-primary animate-pulse" />
      )}
    </NavLink>
  );

  return (
    <nav className="border-b border-border bg-surface pt-safe md:pt-0">
      <div className="flex min-h-14 items-center gap-1 px-4 md:min-h-0">
        {/* Brand logo */}
        <Link
          to="/chat"
          className="mr-4 flex items-center gap-2 transition-opacity hover:opacity-80"
        >
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary">
            <span className="text-sm font-semibold text-primary-foreground">M</span>
          </div>
          <span className="text-sm font-semibold text-text-primary">Maestro-Prime</span>
        </Link>

        {/*
          overflow-x-auto, not squeeze: twelve tabs do not fit at md and this row had
          no overflow handling, so they compressed until labels collided. Scrolling
          keeps every option reachable — and Playwright treats an element scrolled
          inside an overflow container as visible, so the nav contract still holds.
        */}
        <div className="hidden min-w-0 items-center gap-1 overflow-x-auto md:flex">
          {NAV_TABS.map((tab, index) => (
            <Fragment key={tab.to}>
              {startsCluster(index) && (
                <span aria-hidden="true" className="mx-1 h-4 w-px shrink-0 bg-border" />
              )}
              {renderTab(tab)}
            </Fragment>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-text-secondary">
            v{import.meta.env.VITE_APP_VERSION as string}
            {updateCheck?.updateAvailable && updateCheck.releaseUrl && (
              <a
                href={updateCheck.releaseUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1.5 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                title={`v${updateCheck.latestVersion} available`}
              >
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />v
                {updateCheck.latestVersion}
              </a>
            )}
          </span>
          <NotificationBell />
          <span aria-hidden="true" className="hidden h-5 w-px bg-border md:block" />
          {operatorLabel != null && (
            <span
              className="hidden max-w-40 truncate text-xs text-text-secondary lg:block"
              title={operatorLabel}
            >
              {operatorLabel}
            </span>
          )}
          <button
            type="button"
            onClick={(): void => {
              signOut.mutate();
            }}
            disabled={signOut.isPending}
            // The signed-out surface was unreachable from inside the app: there was
            // no sign-out control anywhere, so the only exit was clearing cookies.
            className={cn(
              'flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors disabled:opacity-60',
              signOut.isError
                ? 'text-error hover:bg-surface-hover'
                : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
            )}
            title={
              signOut.isError ? `Sign out failed: ${signOut.error?.message}` : 'Sign out of Maestro'
            }
          >
            <LogOut className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">
              {signOut.isPending
                ? 'Signing out…'
                : signOut.isError
                  ? 'Sign out failed'
                  : 'Sign out'}
            </span>
          </button>
          {/* Announced without stealing focus; the button label alone would not be
              read out again after a failed attempt. */}
          {signOut.isError && (
            <span role="alert" className="sr-only">
              Sign out failed: {signOut.error?.message}
            </span>
          )}
        </div>
      </div>
      <div className="flex gap-1 overflow-x-auto px-2 pb-1 md:hidden" aria-label="Mobile top menu">
        {NAV_TABS.map(renderTab)}
      </div>
    </nav>
  );
}
